import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useSiteAuth, signInWithGoogle } from "@/components/GoogleAuth";
import { Loader2, LogOut, User as UserIcon, Music2, ShoppingBag, CalendarCheck } from "lucide-react";

const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z"/>
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.2 0-5.8-2.1-6.8-5l-3.9 3C3.3 21.3 7.3 24 12 24z"/>
    <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l3.9-3z"/>
    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l3.9 3c1-2.8 3.6-4.9 6.8-4.9z"/>
  </svg>
);

export default function AccountPage() {
  const { user, signOut } = useSiteAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const authError = location.state?.authError;

  useEffect(() => {
    if (user === null && !authError) {
      // stay — show the sign-in prompt
    }
  }, [user, authError]);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    setSigningOut(false);
    navigate("/");
  };

  return (
    <main className="min-h-screen bg-obsidian text-ice" data-testid="account-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 sm:px-8 pt-36 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="bg-charcoal border border-crystal/25 rounded-2xl p-8 sm:p-10 shadow-[0_20px_80px_rgba(0,0,0,0.5)]"
        >
          {user === undefined ? (
            <div className="flex justify-center py-16" data-testid="account-loading">
              <Loader2 className="animate-spin text-venom" size={28} />
            </div>
          ) : user ? (
            <div data-testid="account-profile">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-venom">Member Account</p>
              <div className="mt-5 flex items-center gap-4">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-16 h-16 rounded-full border-2 border-venom/50 object-cover" data-testid="account-avatar" />
                ) : (
                  <span className="w-16 h-16 rounded-full border-2 border-venom/50 bg-obsidian flex items-center justify-center">
                    <UserIcon size={26} className="text-venom" />
                  </span>
                )}
                <div className="min-w-0">
                  <h1 className="font-display text-xl font-bold text-white truncate" data-testid="account-name">{user.name || "Member"}</h1>
                  <p className="text-sm text-ice/55 truncate" data-testid="account-email">{user.email}</p>
                </div>
              </div>
              <div className="mt-8 grid gap-3">
                <Link to="/beats" className="flex items-center gap-3 border border-white/12 rounded-xl px-4 py-3.5 text-sm text-ice/80 hover:border-venom/60 hover:text-venom transition-colors" data-testid="account-link-beats">
                  <Music2 size={16} className="text-venom" /> Browse the beat store
                </Link>
                <Link to="/merch" className="flex items-center gap-3 border border-white/12 rounded-xl px-4 py-3.5 text-sm text-ice/80 hover:border-venom/60 hover:text-venom transition-colors" data-testid="account-link-merch">
                  <ShoppingBag size={16} className="text-venom" /> Shop official merch
                </Link>
                <Link to="/booking" className="flex items-center gap-3 border border-white/12 rounded-xl px-4 py-3.5 text-sm text-ice/80 hover:border-venom/60 hover:text-venom transition-colors" data-testid="account-link-booking">
                  <CalendarCheck size={16} className="text-venom" /> Book a session
                </Link>
              </div>
              <p className="mt-6 text-[11px] text-ice/40 leading-relaxed">
                Order history and saved downloads are coming to this page as the store expands.
              </p>
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] border border-white/15 rounded-full px-5 py-2.5 text-ice/60 hover:border-red-400/60 hover:text-red-400 transition-all"
                data-testid="account-signout-button"
              >
                {signingOut ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                Sign Out
              </button>
            </div>
          ) : (
            <div className="text-center" data-testid="account-signin-prompt">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-venom">Members</p>
              <h1 className="mt-2 font-display text-2xl font-bold text-white">Sign in to DGE</h1>
              <p className="mt-3 text-sm text-ice/55 leading-relaxed">
                One tap with your Google account. Track what&rsquo;s yours and get member-first drops as they roll out.
              </p>
              {authError && (
                <p className="mt-4 text-sm text-red-400" role="alert" data-testid="account-auth-error">
                  Sign-in didn&rsquo;t complete. Please try again.
                </p>
              )}
              <button
                onClick={signInWithGoogle}
                className="mt-7 inline-flex items-center justify-center gap-3 bg-white text-obsidian font-semibold text-sm px-7 py-3.5 rounded-full hover:scale-[1.03] active:scale-95 transition-transform duration-300 w-full sm:w-auto"
                data-testid="account-google-signin"
              >
                <GoogleMark /> Continue with Google
              </button>
              <p className="mt-5 text-[11px] text-ice/35">
                We only receive your name, email and profile photo from Google.
              </p>
            </div>
          )}
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
